import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ListingsAPI } from '@certi/shared/api/listings/ListingsAPI'
import { BiddingAPI, type AuctionInfo, type BidHistoryResponse } from '@certi/shared/api/bidding/BiddingAPI'
import type { ListingResponse } from '@certi/shared/types'
import { useBiddingWebSocket, type BidUpdateMessage } from '../../hooks/useBiddingWebSocket'
import { useAuth } from '@/hooks/useAuth'
import { AuthModal } from '@/components/auth'

interface GameDetails {
  date: string
  venue: string
  opponent: string
}

export default function ListingDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { user, signIn, signUp } = useAuth()
  const [listing, setListing] = useState<ListingResponse | null>(null)
  const [auctionInfo, setAuctionInfo] = useState<AuctionInfo | null>(null)
  const [bidHistory, setBidHistory] = useState<BidHistoryResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [timeRemaining, setTimeRemaining] = useState<string>('')
  const [bidAmount, setBidAmount] = useState<string>('')
  const [placingBid, setPlacingBid] = useState(false)
  const [_isWebSocketConnected, setIsWebSocketConnected] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)

  // Handle real-time bid updates via WebSocket
  const handleBidUpdate = (message: BidUpdateMessage) => {
    console.log('Bid update received:', message)
    
    if (message.type === 'bid_placed' && message.auctionData) {
      // Update auction info with new current price and bid count
      setAuctionInfo(prev => prev ? {
        ...prev,
        currentPrice: message.auctionData!.currentPrice,
        currentLeaderId: message.auctionData!.currentLeaderId,
        status: message.auctionData!.status
      } : null)
      
      // Add new bid to history
      if (message.bidData) {
        const newBidHistoryItem: BidHistoryResponse = {
          bidderId: message.bidData.bidderId,
          amount: message.bidData.amount,
          placedAt: message.bidData.placedAt,
          status: message.bidData.status
        }
        setBidHistory(prev => [newBidHistoryItem, ...prev.slice(0, 9)]) // Keep only top 10
      }
    }
  }

  // Initialize WebSocket connection
  const { isConnected } = useBiddingWebSocket({
    auctionId: auctionInfo?.auctionId,
    onBidUpdate: handleBidUpdate,
    onConnectionChange: setIsWebSocketConnected
  })

  // Countdown timer effect
  useEffect(() => {
    const updateCountdown = () => {
      if (!auctionInfo) return
      
      const timeLeft = BiddingAPI.getTimeRemaining(auctionInfo.endsAt)
      setTimeRemaining(timeLeft)
    }

    if (auctionInfo) {
      updateCountdown()
      const timer = setInterval(updateCountdown, 60000) // Update every minute
      return () => clearInterval(timer)
    }
  }, [auctionInfo])

  useEffect(() => {
    if (!slug) {
      navigate('/marketplace')
      return
    }

    const fetchListingData = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Fetch listing details
        const listingResponse = await ListingsAPI.getListingBySlug(slug)
        setListing(listingResponse)
        
        // Fetch auction information if listing exists
        if (listingResponse?.id) {
          try {
            const auctionResponse = await BiddingAPI.getListingAuction(listingResponse.id)
            setAuctionInfo(auctionResponse)
            
            // Fetch bid history
            const bidHistoryResponse = await BiddingAPI.getBidHistory(auctionResponse.auctionId, 10, 0)
            setBidHistory(bidHistoryResponse)
            
            // Set initial bid amount (minimum bid)
            const nextBidAmount = auctionResponse.currentPrice + 1
            setBidAmount(nextBidAmount.toString())
            
          } catch (auctionErr) {
            console.warn('No auction found for listing:', auctionErr)
            // Not all listings have auctions, this is okay
          }
        }
        
      } catch (err) {
        console.error('Failed to fetch listing:', err)
        setError(err instanceof Error ? err.message : 'Failed to load listing')
      } finally {
        setLoading(false)
      }
    }

    fetchListingData()
  }, [slug, navigate])

  // Mock data for enhanced features (in production, this would come from the API)
  const getGameDetails = (listing: ListingResponse): GameDetails | null => {
    // Generate mock game details based on the listing
    const sportTypeToGameMap: Record<string, GameDetails> = {
      'FOOTBALL': {
        date: 'Feb 3, 2017',
        venue: 'NRG Stadium',
        opponent: 'Atlanta Falcons'
      },
      'BASKETBALL': {
        date: 'Jun 16, 2020',
        venue: 'Staples Center',
        opponent: 'Boston Celtics'
      },
      'BASEBALL': {
        date: 'Oct 27, 2009',
        venue: 'Yankee Stadium',
        opponent: 'Philadelphia Phillies'
      },
      'SOCCER': {
        date: 'Jul 15, 2018',
        venue: 'Luzhniki Stadium',
        opponent: 'Croatia'
      }
    }
    
    return sportTypeToGameMap[listing.sportType] || null
  }


  // Helper functions (removed unused formatCurrency)

  const getTagStyles = (tag: string) => {
    switch (tag) {
      case 'AUTOGRAPHED':
        return { color: 'text-blue-700', bg: 'bg-blue-100' }
      case 'LIMITED_EDITION':
        return { color: 'text-purple-700', bg: 'bg-purple-100' }
      case 'PLAYOFF_GAME':
        return { color: 'text-green-700', bg: 'bg-green-100' }
      case 'FINAL_MATCH':
        return { color: 'text-red-700', bg: 'bg-red-100' }
      case 'DEBUT_GAME':
        return { color: 'text-orange-700', bg: 'bg-orange-100' }
      case 'MVP_GAME':
        return { color: 'text-yellow-700', bg: 'bg-yellow-100' }
      default:
        return { color: 'text-gray-700', bg: 'bg-gray-100' }
    }
  }


  const handlePlaceBid = async () => {
    // Check if user is authenticated
    if (!user) {
      setShowAuthModal(true)
      return
    }

    if (!auctionInfo || !bidAmount) return
    
    try {
      setPlacingBid(true)
      
      const bidAmountNum = parseFloat(bidAmount)
      if (bidAmountNum <= auctionInfo.currentPrice) {
        alert(`Bid must be higher than current price of ${BiddingAPI.formatCurrency(auctionInfo.currentPrice)}`)
        return
      }
      
      await BiddingAPI.placeBid({
        auctionId: auctionInfo.auctionId,
        amount: bidAmountNum
      })
      
      // Set next minimum bid amount optimistically
      const nextBidAmount = bidAmountNum + 1
      setBidAmount(nextBidAmount.toString())
      
      // Show success message
      alert('Bid placed successfully! Updates will appear automatically.')
      
    } catch (err) {
      console.error('Failed to place bid:', err)
      alert(err instanceof Error ? err.message : 'Failed to place bid')
    } finally {
      setPlacingBid(false)
    }
  }

  // Handle social login (placeholder implementation)
  const handleSocialLogin = async (provider: 'google' | 'apple') => {
    try {
      // This would integrate with actual social auth providers
      console.log(`Social login with ${provider}`)
      alert(`${provider} login integration coming soon!`)
    } catch (err) {
      throw new Error(`Failed to login with ${provider}`)
    }
  }

  const handlePrevImage = () => {
    if (listing?.mediaUrls && listing.mediaUrls.length > 1) {
      setCurrentImageIndex(prev => 
        prev === 0 ? listing.mediaUrls.length - 1 : prev - 1
      )
    }
  }

  const handleNextImage = () => {
    if (listing?.mediaUrls && listing.mediaUrls.length > 1) {
      setCurrentImageIndex(prev => 
        prev === listing.mediaUrls.length - 1 ? 0 : prev + 1
      )
    }
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-200 aspect-square rounded-lg"></div>
            <div className="space-y-6">
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              <div className="h-12 bg-gray-200 rounded w-1/2"></div>
              <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !listing) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {error === 'Listing not found' ? 'Listing Not Found' : 'Error Loading Listing'}
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/marketplace')}
            className="btn btn-primary"
          >
            Back to Marketplace
          </button>
        </div>
      </div>
    )
  }

  // Component functions for better organization
  const gameDetails = listing ? getGameDetails(listing) : null
  const hasMultipleImages = listing?.mediaUrls && listing.mediaUrls.length > 1

  // Media Viewer Component - Desktop Layout with Carousel
  const MediaViewer = () => (
    <div className="w-full">
      {hasMultipleImages ? (
        /* Desktop Layout: Thumbnails + Main Image */
        <div className="flex gap-4">
          {/* Left: Thumbnail Carousel */}
          <div className="flex flex-col space-y-3 w-20">
            {listing?.mediaUrls?.map((url, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                  index === currentImageIndex 
                    ? 'border-brand-primary shadow-md' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <img
                  src={url}
                  alt={`${listing.title} ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>

          {/* Right: Main Image */}
          <div className="flex-1">
            <div className="relative aspect-[4/3] bg-gray-100 rounded-xl overflow-hidden shadow-lg">
              <img
                src={listing?.mediaUrls?.[currentImageIndex]}
                alt={listing?.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                  target.parentElement!.innerHTML = `
                    <div class="w-full h-full flex items-center justify-center bg-gray-100">
                      <svg class="w-16 h-16 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                      </svg>
                    </div>
                  `
                }}
              />
              
              {/* Navigation arrows */}
              <button
                onClick={handlePrevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-75 transition-all"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-75 transition-all"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Single Image Layout */
        <div className="relative aspect-[4/3] bg-gray-100 rounded-xl overflow-hidden shadow-lg">
          {listing?.mediaUrls && listing.mediaUrls.length > 0 ? (
            <img
              src={listing.mediaUrls[0]}
              alt={listing.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
                target.parentElement!.innerHTML = `
                  <div class="w-full h-full flex items-center justify-center bg-gray-100">
                    <svg class="w-16 h-16 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                    </svg>
                  </div>
                `
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg className="w-16 h-16 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
              </svg>
            </div>
          )}
        </div>
      )}
    </div>
  )

  // Item Title Section Component
  const ItemTitleSection = () => (
    <div className="space-y-4">
      {listing?.teamOrClub && (
        <p className="text-lg text-gray-600 font-medium">{listing.teamOrClub}</p>
      )}
      <h1 className="text-4xl font-bold text-gray-900 leading-tight">
        {listing?.title}
      </h1>
      {listing?.playerName && (
        <p className="text-2xl font-semibold text-gray-800">
          {listing.playerName}
        </p>
      )}

      {/* Badges - mobile style */}
      <div className="flex flex-wrap gap-2">
        {/* NFC Badge */}
        {listing?.nfcUid && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            📱 NFC Verified
          </span>
        )}
        {/* Certi Authenticated Badge */}
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          ✓ Certi Authenticated
        </span>
        {/* Dynamic tags */}
        {listing?.tags?.map((tag) => {
          const styles = getTagStyles(tag)
          return (
            <span
              key={tag}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${styles.color} ${styles.bg}`}
            >
              {tag.replace(/_/g, ' ')}
            </span>
          )
        })}
      </div>
    </div>
  )

  // Enhanced Bid Section Component 
  const BidSection = () => {
    if (!auctionInfo) {
      return (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg">
          <div className="text-center py-8">
            <p className="text-gray-600 text-lg">No auction available for this item</p>
            <p className="text-sm text-gray-500 mt-2">This item may be available for direct purchase</p>
          </div>
        </div>
      )
    }
    
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg">
        <div className="space-y-6">
          {/* Current Bid */}
          <div>
            <p className="text-gray-600 text-lg font-medium mb-2">Current Bid</p>
            <p className="text-5xl font-bold text-gray-900">
              {BiddingAPI.formatCurrency(auctionInfo.currentPrice)}
            </p>
          </div>

          {/* Auction Meta */}
          <div className="flex items-center justify-between text-gray-600">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <circle cx={12} cy={12} r={10} />
                <polyline points="12,6 12,12 16,14" />
              </svg>
              <span className="font-medium">
                Ends {timeRemaining || 'Loading...'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx={9} cy={7} r={4} />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span className="font-medium">
                {bidHistory.length} bid{bidHistory.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {/* Bid Input */}
          {auctionInfo.isActive && timeRemaining !== 'Ended' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Bid Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-500">$</span>
                <input
                  type="number"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  min={auctionInfo.currentPrice + 1}
                  step="1"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  placeholder="Enter bid amount"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Minimum bid: {BiddingAPI.formatCurrency(auctionInfo.currentPrice + 1)}
              </p>
            </div>
          )}

          {/* Place Bid Button */}
          <button
            onClick={handlePlaceBid}
            disabled={timeRemaining === 'Ended' || placingBid || (!user && !auctionInfo.isActive)}
            className={`w-full py-4 px-6 rounded-xl text-lg font-semibold transition-all duration-300 ${
              auctionInfo.isActive && timeRemaining !== 'Ended' && !placingBid
                ? 'bg-brand-primary text-white hover:bg-brand-primary/90 shadow-lg hover:shadow-xl hover:scale-105'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {placingBid ? 'Placing Bid...' : 
             timeRemaining === 'Ended' ? 'Auction Ended' : 
             !user ? '🔒 Sign In to Bid' : 'Place Bid'}
          </button>
          
          {/* Auction Status */}
          <div className="text-center space-y-2">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              auctionInfo.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
              auctionInfo.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {auctionInfo.status === 'ACTIVE' ? '🔴 Live Auction' : 
               auctionInfo.status === 'PENDING' ? '⏳ Starting Soon' :
               auctionInfo.status}
            </span>
            
            {/* WebSocket Connection Status */}
            <div className="flex items-center justify-center space-x-1">
              <div className={`w-2 h-2 rounded-full ${
                isConnected ? 'bg-green-500' : 'bg-red-500'
              }`} />
              <span className="text-xs text-gray-500">
                {isConnected ? 'Live updates active' : 'Connecting...'}
              </span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Description Block Component
  const DescriptionBlock = () => {
    if (!listing?.description) return null
    
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Description</h3>
        <p className="text-gray-700 leading-relaxed text-lg">
          {listing.description}
        </p>
      </div>
    )
  }

  // Game Details Section Component (mobile-inspired)
  const GameDetailsSection = () => {
    if (!gameDetails) return null
    
    const DetailRow = ({ icon, label, value }: { icon: string, label: string, value: string }) => (
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0 w-6 h-6 text-gray-500 mt-1">
          <div dangerouslySetInnerHTML={{ __html: icon }} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500 mb-1">{label}</p>
          <p className="text-lg font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    )

    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Game Details</h3>
        <div className="space-y-6">
          <DetailRow
            icon={`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`}
            label="Game Date"
            value={gameDetails.date}
          />
          <DetailRow
            icon={`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`}
            label="Venue"
            value={gameDetails.venue}
          />
          <DetailRow
            icon={`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`}
            label="Opponent"
            value={gameDetails.opponent}
          />
        </div>
      </div>
    )
  }

  // Player Stats Section Component (mobile-inspired)
  const PlayerStatsSection = () => {
    if (!listing?.playerName) return null
    
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Player Stats</h3>
        <div className="text-center py-8 space-y-3">
          <p className="text-lg font-medium text-gray-600">
            Player statistics will be available soon
          </p>
          <p className="text-sm text-gray-500">
            {listing.playerName} career highlights coming soon
          </p>
        </div>
      </div>
    )
  }

  // Item Details Component
  const ItemDetailsSection = () => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">Item Details</h3>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-gray-600 font-medium">Category</span>
          <span className="font-semibold text-gray-900">{listing?.category.replace('_', ' ')}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600 font-medium">Condition</span>
          <span className="font-semibold text-gray-900">{listing?.condition.replace('_', ' ')}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600 font-medium">Sport</span>
          <span className="font-semibold text-gray-900">{listing?.sportType}</span>
        </div>
        {listing?.size && (
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-medium">Size</span>
            <span className="font-semibold text-gray-900">{listing.size}</span>
          </div>
        )}
        {listing?.leagueOrEvent && (
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-medium">League/Event</span>
            <span className="font-semibold text-gray-900">{listing.leagueOrEvent}</span>
          </div>
        )}
        {listing?.dateOrSeason && (
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-medium">Date/Season</span>
            <span className="font-semibold text-gray-900">{listing.dateOrSeason}</span>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/marketplace')}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">Back to Marketplace</span>
          </button>
        </div>
      </div>

      {/* Main Content - Desktop Layout */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Top Section: Image Gallery + Bidding */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Left: Image Gallery (2/3 width) */}
          <div className="lg:col-span-2">
            <MediaViewer />
          </div>

          {/* Right: Bidding Section (1/3 width) */}
          <div className="lg:col-span-1">
            {/* Item Title Section */}
            <div className="mb-6">
              <ItemTitleSection />
            </div>
            
            {/* Bid Section */}
            <div className="sticky top-24">
              <BidSection />
            </div>
          </div>
        </div>

        {/* Bottom Section: Info Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Description */}
          <div className="lg:col-span-2">
            <DescriptionBlock />
          </div>

          {/* Game Details */}
          <div className="lg:col-span-1">
            <GameDetailsSection />
          </div>
        </div>

        {/* Additional Info Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          {/* Player Stats */}
          <PlayerStatsSection />

          {/* Item Details */}
          <ItemDetailsSection />
        </div>

        {/* Bottom Padding */}
        <div className="h-12" />
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLogin={async (email: string, password: string) => {
          await signIn({ email, password })
        }}
        onRegister={async (email: string, password: string, name: string) => {
          const [firstName, ...lastNameParts] = name.split(' ')
          const lastName = lastNameParts.join(' ') || firstName
          await signUp({ firstName, lastName, email, password })
        }}
        onSocialLogin={handleSocialLogin}
      />
    </div>
  )
}